import { useState } from "react";
import { MapPin, Plus, Route as RouteIcon, X } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal } from "@/components/motion";
import { CHRISTMAS_WALK_ROUTE, christmasWalkUrl } from "@/data/christmasWalk";

// The interactive Christmas walk card — shared by /christmas (as a section)
// and /christmas-walk (the dedicated page). Stops can be dropped and
// re-added; the Google Maps walking link always follows the remaining stops.
export const ChristmasWalkCard = ({ linkToGuide = false }) => {
  const [skipped, setSkipped] = useState([]);
  const stops = CHRISTMAS_WALK_ROUTE.filter((s) => !skipped.includes(s.name));

  return (
    <Reveal y={30}>
      <div
        data-testid="christmas-walk-route"
        className="bg-[#F9F8F6] border border-zinc-200 shadow-brut rounded-2xl p-8 md:p-12"
      >
        <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226] mb-3">
          ONE WALK · MARKETS TO TIVOLI
        </p>
        <div className="flex flex-col lg:flex-row lg:items-center gap-8 justify-between">
          <div className="flex-1">
            <h2 className="font-display font-900 tracking-tighter leading-[0.95] text-3xl md:text-5xl">
              The Christmas walk,{" "}
              <span className="font-editorial italic font-600">ready-made</span>
            </h2>
            <p className="font-body text-sm md:text-base text-black/60 mt-3 max-w-lg">
              The Nyhavn market, past the d'Angleterre's, gløgg at Hviids Vinstue (the wine
              cellar dating back to 1723), the Christmas markets and then Tivoli as darkness
              falls. About 2.5 km — roughly three and a half hours with stops.
            </p>
            <p className="font-body text-xs text-black/40 mt-2">
              Tap ✕ on any stop to drop it — the Maps route updates.
              {linkToGuide && (
                <>
                  {" "}
                  <Link
                    to="/christmas-walk"
                    data-testid="christmas-walk-guide-link"
                    className="inline-flex items-center gap-0.5 font-800 text-[#DF3226] underline underline-offset-2 hover:text-black transition-colors"
                  >
                    The full walk guide
                  </Link>
                </>
              )}
            </p>
            <ol className="flex flex-wrap gap-2 mt-5">
              {stops.map((s, i) => (
                <li
                  key={s.name}
                  data-testid={`christmas-walk-stop-${i}`}
                  className="inline-flex items-center gap-1.5 font-body font-700 text-xs bg-white border border-zinc-200 rounded-full pl-3 pr-2 py-1.5"
                >
                  <span className="font-display font-900 text-[#DF3226]">{i + 1}</span> {s.name}
                  <span className="font-body font-600 text-[10px] text-black/35">
                    · {s.durText}
                  </span>
                  <button
                    type="button"
                    data-testid={`christmas-walk-remove-${i}`}
                    aria-label={`Remove ${s.name} from the route`}
                    onClick={() => setSkipped((p) => [...p, s.name])}
                    className="ml-0.5 rounded-full p-0.5 text-black/30 hover:text-[#DF3226] hover:bg-black/5 transition-colors"
                  >
                    <X size={12} />
                  </button>
                </li>
              ))}
            </ol>
            {skipped.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <span className="font-body text-xs text-black/40">Off the route:</span>
                {CHRISTMAS_WALK_ROUTE.filter((s) => skipped.includes(s.name)).map((s) => (
                  <button
                    key={s.name}
                    type="button"
                    data-testid={`christmas-walk-readd-${s.name.replace(/[^a-zA-ZæøåÆØÅ]+/g, "-")}`}
                    onClick={() => setSkipped((p) => p.filter((n) => n !== s.name))}
                    className="inline-flex items-center gap-1 font-body font-700 text-xs text-black/40 border border-dashed border-zinc-300 rounded-full px-3 py-1.5 hover:text-black hover:border-zinc-400 transition-colors"
                  >
                    <Plus size={11} /> {s.name}
                  </button>
                ))}
              </div>
            )}
          </div>
          {stops.length >= 2 && (
            <a
              href={christmasWalkUrl(stops)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="christmas-walk-open-btn"
              className="shrink-0 inline-flex items-center gap-2 bg-[#FDE047] text-[#13141A] font-body font-800 text-sm px-5 py-3 rounded-full hover:bg-white transition-colors"
            >
              <MapPin size={16} /> Open the whole route in Google Maps
            </a>
          )}
        </div>
      </div>
    </Reveal>
  );
};
